import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useUserAuth } from "../context/UserAuthContext";
import { db } from '../config/fbConfig';
import { collection, getDocs, query, where } from 'firebase/firestore';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import Loader from "./Loader";
import autoTable from 'jspdf-autotable';

import ShopLogo from "../images/shop_logo.png";
import { 
  SHOP_NAME, 
  SHOP_ADDRESS, 
  SHOP_PHONE,
} from '../Variables.js';

const Salary = () => {
  const { userData } = useUserAuth();
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    staff: [],
    ranks: [],
    salaryComponents: [],
    payrolls: {},
    departments: []
  });
  const [openDept, setOpenDept] = useState(null); // null, department name, or 'all'

  useEffect(() => {
    const now = new Date();
    setSelectedMonth(String(now.getMonth() + 1).padStart(2, '0'));
    setSelectedYear(now.getFullYear());
  }, []);

  useEffect(() => {
    if (selectedMonth && selectedYear) fetchPayrollData();
  }, [selectedMonth, selectedYear]);

  const fetchPayrollData = async () => {
    if (!selectedMonth || !selectedYear) return;
    setLoading(true);
    const monthYear = `${selectedYear}-${selectedMonth}`;

    try {
      const [staffSnap, ranksSnap, compSnap, payrollSnap] = await Promise.all([
        getDocs(query(collection(db, "users"), where("isActive", "==", true))),
        getDocs(collection(db, "ranks")),
        getDocs(collection(db, "salaryStructure")),
        getDocs(query(collection(db, "payrollRecords"), where("monthYear", "==", monthYear)))
      ]);

      const staff = staffSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const ranks = ranksSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const components = compSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const payrolls = {};
      payrollSnap.docs.forEach(d => {
        const data = d.data();
        payrolls[data.staffId] = { extras: data.extras || [] };
      });

      const depts = [...new Set(staff.map(s => s.department || "Unassigned"))];
      setData({ staff, ranks, salaryComponents: components, payrolls, departments: depts });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getRank = (id) => data.ranks.find(r => r.id === id) || {};
  const getComponent = (id) => data.salaryComponents.find(c => c.id === id) || {};

  const calculatePay = (staff) => {
    const rank = getRank(staff.rank);
    const basic = parseFloat(rank.basicSalary) || 0;
    let gross = basic;
    let deductions = 0;
    const items = { basic };

    rank.components?.forEach(c => {
      const comp = getComponent(c.id);
      const val = parseFloat(c.value) || 0;
      if (!comp) return;
      const amt = comp.calculation === 'percentage'
        ? (comp.percentageOf === 'basic' ? basic * val / 100 : gross * val / 100)
        : val;
      if (comp.type === 'addition') {
        gross += amt;
        items[comp.name.toUpperCase()] = amt;
      } else {
        deductions += amt;
        items[comp.name.toUpperCase()] = -amt;
      }
    });

    const extras = data.payrolls[staff.id]?.extras || [];
    extras.forEach(e => {
      const val = parseFloat(e.value) || 0;
      const amt = e.calculation === 'percentage'
        ? (e.percentageOf === 'basic' ? basic * val / 100 : gross * val / 100)
        : val;
      if (e.type === 'addition') {
        gross += amt;
        items[e.name.toUpperCase()] = amt;
      } else {
        deductions += amt;
        items[e.name.toUpperCase()] = -amt;
      }
    });

    const net = gross - deductions;
    return { basic, gross, deductions, net, items };
  };

  const staffInDept = (dept) => data.staff.filter(s => (s.department || "Unassigned") === dept);

  const monthName = new Date(selectedYear, selectedMonth - 1).toLocaleString('default', { month: 'long' });
  const generatedDate = new Date().toLocaleDateString('en-GB', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit'
  }).replace(/,/g, '');

  const addHeader = (doc) => {
    doc.setFillColor(226, 232, 240);
    doc.rect(0, 0, doc.internal.pageSize.width, 50, 'F');
    
    try {
      doc.addImage(ShopLogo, 'PNG', 15, 8, 38, 38);
    } catch (err) {
      console.log("Logo not loaded");
    }
    
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text(SHOP_NAME.toUpperCase(), 60, 20);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(SHOP_ADDRESS.toUpperCase(), 60, 28);
    doc.text(SHOP_PHONE, 60, 35);

    doc.setTextColor(0, 0, 0);
  };

  // ====================== PER DEPARTMENT PDF FUNCTIONS (UNCHANGED) ======================

  const generatePDFPaySlips = (dept) => {
    const doc = new jsPDF();
    const staffList = staffInDept(dept);

    staffList.forEach((staff, index) => {
      if (index > 0) doc.addPage();
      const { gross, deductions, net, items } = calculatePay(staff);

      addHeader(doc);

      doc.setFillColor(245, 245, 245);
      doc.rect(15, 55, 180, 30, 'F');
      
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('STAFF INFORMATION', 20, 65);
      
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.text(`Name: ${staff.firstName.toUpperCase()} ${staff.lastName.toUpperCase()}`, 20, 72);
      doc.text(`Department: ${staff.department || 'N/A'}`, 20, 77);
      doc.text(`Bank: ${staff.bankName || 'N/A'}`, 100, 72);
      doc.text(`Account: ${staff.accountNumber || 'N/A'}`, 100, 77);
      doc.text(`Period: ${monthName}, ${selectedYear}`, 150, 77);

      let y = 95;

      const hasEarnings = Object.entries(items).some(([_, amt]) => amt > 0);
      if (hasEarnings) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.text('EARNINGS', 15, y);
        y += 8;

        const earningsData = Object.entries(items)
          .filter(([_, amt]) => amt > 0)
          .map(([name, amt], idx) => [
            idx + 1,
            name.toUpperCase(),
            `N${amt.toLocaleString('en-US', { minimumFractionDigits: 2 })}`
          ]);

        autoTable(doc, {
          startY: y,
          head: [['S/N', 'Description', 'Amount']],
          body: earningsData,
          theme: 'grid',
          styles: { fontSize: 9, cellPadding: 2, textColor: 0 },
          headStyles: { fillColor: [41, 128, 185], textColor: 255, fontStyle: 'bold' },
          columnStyles: {
            0: { cellWidth: 15, halign: 'center' },
            1: { cellWidth: 125 },
            2: { cellWidth: 40, halign: 'right' }
          },
          margin: { left: 15, right: 15 }
        });

        y = doc.lastAutoTable.finalY + 10;
      }

      const hasDeductions = Object.entries(items).some(([_, amt]) => amt < 0);
      if (hasDeductions) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.text('DEDUCTIONS', 15, y);
        y += 8;

        const deductionsData = Object.entries(items)
          .filter(([_, amt]) => amt < 0)
          .map(([name, amt], idx) => [
            idx + 1,
            name.toUpperCase(),
            `N${(-amt).toLocaleString('en-US', { minimumFractionDigits: 2 })}`
          ]);

        autoTable(doc, {
          startY: y,
          head: [['S/N', 'Description', 'Amount']],
          body: deductionsData,
          theme: 'grid',
          styles: { fontSize: 9, cellPadding: 2, textColor: 0 },
          headStyles: { fillColor: [231, 76, 60], textColor: 255, fontStyle: 'bold' },
          columnStyles: {
            0: { cellWidth: 15, halign: 'center' },
            1: { cellWidth: 125 },
            2: { cellWidth: 40, halign: 'right' }
          },
          margin: { left: 15, right: 15 }
        });

        y = doc.lastAutoTable.finalY + 10;
      }

      doc.setFillColor(71, 85, 105);
      doc.rect(15, y, 180, 28, 'F');
      
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text('PAYMENT SUMMARY', 20, y + 8);

      doc.setFontSize(10);
      doc.text('Gross Pay:', 120, y + 8);
      doc.text(`N${gross.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, 180, y + 8, { align: 'right' });
      
      doc.text('Total Deductions:', 120, y + 15);
      doc.text(`N${deductions.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, 180, y + 15, { align: 'right' });
      
      doc.setFontSize(14);
      doc.text('Net Pay:', 120, y + 22);
      doc.text(`N${net.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, 180, y + 22, { align: 'right' });

      doc.setTextColor(100, 100, 100);
      doc.setFontSize(8);
      doc.text(`Generated on: ${generatedDate}`, 105, 285, { align: 'center' });
      doc.text(`Page ${index + 1} of ${staffList.length}`, 195, 285, { align: 'right' });
    });

    doc.save(`PaySlips_${dept}_${monthName}_${selectedYear}.pdf`);
  };

  const generateBankSummary = (dept) => {
    const doc = new jsPDF();
    addHeader(doc);

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(41, 128, 185);
    doc.text(`SALARY BANK SUMMARY - ${monthName.toUpperCase()} ${selectedYear}`, 105, 60, { align: 'center' });

    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    doc.text(`Department: ${dept.toUpperCase()}`, 15, 70);

    const body = staffInDept(dept).map((s, i) => [
      i + 1,
      `${s.firstName.toUpperCase()} ${s.lastName.toUpperCase()}`,
      s.bankName?.toUpperCase() || '---',
      s.accountNumber || '---',
      `N${calculatePay(s).net.toLocaleString('en-US', { minimumFractionDigits: 2 })}`
    ]);

    const total = staffInDept(dept).reduce((sum, s) => sum + calculatePay(s).net, 0);
    body.push(['', '', '', 'TOTAL', `N${total.toLocaleString('en-US', { minimumFractionDigits: 2 })}`]);

    autoTable(doc, {
      startY: 77,
      head: [['S/N', 'Staff Name', 'Bank', 'Acc Number', 'Net Amount']],
      body,
      theme: 'grid',
      styles: { fontSize: 9, cellPadding: 2, lineColor: [200, 200, 200], lineWidth: 0.1, textColor: 0 },
      headStyles: { fillColor: [41, 128, 185], textColor: 255, fontStyle: 'bold', fontSize: 10 },
      columnStyles: {
        0: { cellWidth: 15, halign: 'center' },
        1: { cellWidth: 65 },
        2: { cellWidth: 40 },
        3: { cellWidth: 35 },
        4: { cellWidth: 35, halign: 'right' }
      },
      didParseCell: function(data) {
        if (data.row.index === body.length - 1) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = [241, 241, 241];
        }
      },
      margin: { left: 5, right: 5 }
    });

    doc.setFontSize(10);
    doc.text('Authorized Signature: _________________________', 15, doc.lastAutoTable.finalY + 20);
    doc.text(`Total Department Pay: N${total.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, 15, doc.lastAutoTable.finalY + 30);
    doc.text(`Generated: ${generatedDate}`, 105, 285, { align: 'center' });

    doc.save(`BankSummary_${dept}_${monthName}_${selectedYear}.pdf`);
  };

  const generateBreakdown = (dept) => {
    const doc = new jsPDF('landscape');
    addHeader(doc);

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(41, 128, 185);
    doc.text(`DETAILED SALARY BREAKDOWN - ${monthName.toUpperCase()} ${selectedYear}`, 148, 55, { align: 'center' });
    
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    doc.text(`Department: ${dept.toUpperCase()}`, 15, 60);

    const allItems = new Set();
    staffInDept(dept).forEach(s => {
      const { items } = calculatePay(s);
      Object.keys(items).forEach(k => {
        if (k.toUpperCase() !== 'BASIC') allItems.add(k.toUpperCase());
      });
    });

    const earningsHeaders = ['BASIC'];
    Array.from(allItems).forEach(item => {
      if (staffInDept(dept).some(s => (calculatePay(s).items[item] || 0) > 0)) {
        earningsHeaders.push(item);
      }
    });

    const deductionsHeaders = Array.from(allItems)
      .filter(item => staffInDept(dept).some(s => (calculatePay(s).items[item] || 0) < 0))
      .sort();

    const headers = [
      'S/N', 'Employee Name',
      ...earningsHeaders,
      'GROSS PAY',
      ...deductionsHeaders,
      'TOTAL DEDUCTIONS', 'NET PAY'
    ];

    const body = staffInDept(dept).map((s, i) => {
      const { gross, deductions, net, items, basic } = calculatePay(s);
      const row = [
        i + 1,
        `${s.firstName.toUpperCase()} ${s.lastName.toUpperCase()}`
      ];

      earningsHeaders.forEach(header => {
        const amount = header === 'BASIC' ? basic : (items[header] || 0);
        row.push(`N${amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}`);
      });

      row.push(`N${gross.toLocaleString('en-US', { minimumFractionDigits: 2 })}`);

      deductionsHeaders.forEach(header => {
        const amount = items[header] || 0;
        row.push(`N${(amount < 0 ? -amount : 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`);
      });

      row.push(
        `N${deductions.toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
        `N${net.toLocaleString('en-US', { minimumFractionDigits: 2 })}`
      );

      return row;
    });

    // Total row
    const totalRow = ['', 'TOTAL'];
    earningsHeaders.forEach(header => {
      const sum = header === 'BASIC'
        ? staffInDept(dept).reduce((acc, s) => acc + (calculatePay(s).basic || 0), 0)
        : staffInDept(dept).reduce((acc, s) => acc + (calculatePay(s).items[header] || 0), 0);
      totalRow.push(`N${sum.toLocaleString('en-US', { minimumFractionDigits: 2 })}`);
    });

    const grossTotal = staffInDept(dept).reduce((a, s) => a + calculatePay(s).gross, 0);
    totalRow.push(`N${grossTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}`);

    deductionsHeaders.forEach(header => {
      const sum = staffInDept(dept).reduce((acc, s) => {
        const amt = calculatePay(s).items[header] || 0;
        return acc + (amt < 0 ? -amt : 0);
      }, 0);
      totalRow.push(`N${sum.toLocaleString('en-US', { minimumFractionDigits: 2 })}`);
    });

    const deductionsTotal = staffInDept(dept).reduce((a, s) => a + calculatePay(s).deductions, 0);
    const netTotal = staffInDept(dept).reduce((a, s) => a + calculatePay(s).net, 0);
    totalRow.push(
      `N${deductionsTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
      `N${netTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}`
    );

    body.push(totalRow);

    const earningsCount = earningsHeaders.length;
    const deductionsCount = deductionsHeaders.length;

    const columnStyles = {
      0: { cellWidth: 10, halign: 'center' },
      1: { cellWidth: 60 },
      ...Object.fromEntries(Array.from({ length: earningsCount }, (_, i) => [2 + i, { halign: 'right', fillColor: [220, 237, 200] }])),
      [2 + earningsCount]: { halign: 'right', fontStyle: 'bold' },
      ...Object.fromEntries(Array.from({ length: deductionsCount }, (_, i) => [3 + earningsCount + i, { halign: 'right', fillColor: [245, 220, 220] }])),
      ...Object.fromEntries(Array.from({ length: 2 }, (_, i) => [3 + earningsCount + deductionsCount + i, { halign: 'right', fontStyle: 'bold' }]))
    };

    autoTable(doc, {
      startY: 65,
      head: [headers],
      body,
      theme: 'grid',
      styles: { fontSize: 7, cellPadding: 2, lineColor: [200, 200, 200], lineWidth: 0.1, textColor: 0 },
      headStyles: { fillColor: [41, 128, 185], textColor: 255, fontStyle: 'bold', fontSize: 8 },
      columnStyles,
      margin: { left: 5, right: 5 },
      didParseCell: function(data) {
        if (data.row.index === body.length - 1) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = [241, 241, 241];
        }
        const grossIdx = 2 + earningsCount;
        const dedTotIdx = 3 + earningsCount + deductionsCount;
        const netIdx = 4 + earningsCount + deductionsCount;
        if ([grossIdx, dedTotIdx, netIdx].includes(data.column.index)) {
          data.cell.styles.fontStyle = 'bold';
        }
      }
    });

    doc.setFontSize(9);
    doc.text('Authorized Signature: _________________________', 15, doc.lastAutoTable.finalY + 20);
    doc.text(`Generated: ${generatedDate}`, 148, 200, { align: 'center' });

    doc.save(`Breakdown_${dept}_${monthName}_${selectedYear}.pdf`);
  };

  // ====================== ALL DEPARTMENTS COMBINED PDF FUNCTIONS ======================

  const generatePDFPaySlipsAll = () => {
    const doc = new jsPDF();
    const sortedStaff = [...data.staff].sort((a, b) => {
      const deptA = a.department || "Unassigned";
      const deptB = b.department || "Unassigned";
      if (deptA !== deptB) return deptA.localeCompare(deptB);
      return (a.firstName + " " + a.lastName).localeCompare(b.firstName + " " + b.lastName);
    });

    sortedStaff.forEach((staff, index) => {
      if (index > 0) doc.addPage();
      const { gross, deductions, net, items } = calculatePay(staff);

      addHeader(doc);

      doc.setFillColor(245, 245, 245);
      doc.rect(15, 55, 180, 30, 'F');
      
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('STAFF INFORMATION', 20, 65);
      
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.text(`Name: ${staff.firstName.toUpperCase()} ${staff.lastName.toUpperCase()}`, 20, 72);
      doc.text(`Department: ${staff.department || 'N/A'}`, 20, 77);
      doc.text(`Bank: ${staff.bankName || 'N/A'}`, 100, 72);
      doc.text(`Account: ${staff.accountNumber || 'N/A'}`, 100, 77);
      doc.text(`Period: ${monthName}, ${selectedYear}`, 150, 77);

      let y = 95;

      const hasEarnings = Object.entries(items).some(([_, amt]) => amt > 0);
      if (hasEarnings) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.text('EARNINGS', 15, y);
        y += 8;

        const earningsData = Object.entries(items)
          .filter(([_, amt]) => amt > 0)
          .map(([name, amt], idx) => [
            idx + 1,
            name.toUpperCase(),
            `N${amt.toLocaleString('en-US', { minimumFractionDigits: 2 })}`
          ]);

        autoTable(doc, {
          startY: y,
          head: [['S/N', 'Description', 'Amount']],
          body: earningsData,
          theme: 'grid',
          styles: { fontSize: 9, cellPadding: 2, textColor: 0 },
          headStyles: { fillColor: [41, 128, 185], textColor: 255, fontStyle: 'bold' },
          columnStyles: {
            0: { cellWidth: 15, halign: 'center' },
            1: { cellWidth: 125 },
            2: { cellWidth: 40, halign: 'right' }
          },
          margin: { left: 15, right: 15 }
        });

        y = doc.lastAutoTable.finalY + 10;
      }

      const hasDeductions = Object.entries(items).some(([_, amt]) => amt < 0);
      if (hasDeductions) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.text('DEDUCTIONS', 15, y);
        y += 8;

        const deductionsData = Object.entries(items)
          .filter(([_, amt]) => amt < 0)
          .map(([name, amt], idx) => [
            idx + 1,
            name.toUpperCase(),
            `N${(-amt).toLocaleString('en-US', { minimumFractionDigits: 2 })}`
          ]);

        autoTable(doc, {
          startY: y,
          head: [['S/N', 'Description', 'Amount']],
          body: deductionsData,
          theme: 'grid',
          styles: { fontSize: 9, cellPadding: 2, textColor: 0 },
          headStyles: { fillColor: [231, 76, 60], textColor: 255, fontStyle: 'bold' },
          columnStyles: {
            0: { cellWidth: 15, halign: 'center' },
            1: { cellWidth: 125 },
            2: { cellWidth: 40, halign: 'right' }
          },
          margin: { left: 15, right: 15 }
        });

        y = doc.lastAutoTable.finalY + 10;
      }

      doc.setFillColor(71, 85, 105);
      doc.rect(15, y, 180, 28, 'F');
      
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text('PAYMENT SUMMARY', 20, y + 8);

      doc.setFontSize(10);
      doc.text('Gross Pay:', 120, y + 8);
      doc.text(`N${gross.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, 180, y + 8, { align: 'right' });
      
      doc.text('Total Deductions:', 120, y + 15);
      doc.text(`N${deductions.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, 180, y + 15, { align: 'right' });
      
      doc.setFontSize(14);
      doc.text('Net Pay:', 120, y + 22);
      doc.text(`N${net.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, 180, y + 22, { align: 'right' });

      doc.setTextColor(100, 100, 100);
      doc.setFontSize(8);
      doc.text(`Generated on: ${generatedDate}`, 105, 285, { align: 'center' });
      doc.text(`Page ${index + 1} of ${sortedStaff.length}`, 195, 285, { align: 'right' });
    });

    doc.save(`PaySlips_All_Departments_${monthName}_${selectedYear}.pdf`);
  };

  const generateBankSummaryAll = () => {
    const doc = new jsPDF();
    addHeader(doc);

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(41, 128, 185);
    doc.text(`SALARY BANK SUMMARY - ${monthName.toUpperCase()} ${selectedYear}`, 105, 60, { align: 'center' });

    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    doc.text(`All Departments`, 15, 70);

    const sortedStaff = [...data.staff].sort((a, b) => {
      const deptA = a.department || "Unassigned";
      const deptB = b.department || "Unassigned";
      if (deptA !== deptB) return deptA.localeCompare(deptB);
      return (a.firstName + " " + a.lastName).localeCompare(b.firstName + " " + b.lastName);
    });

    const body = sortedStaff.map((s, i) => [
      i + 1,
      `${s.firstName.toUpperCase()} ${s.lastName.toUpperCase()}`,
      s.department || 'N/A',
      s.bankName?.toUpperCase() || '---',
      s.accountNumber || '---',
      `N${calculatePay(s).net.toLocaleString('en-US', { minimumFractionDigits: 2 })}`
    ]);

    const total = data.staff.reduce((sum, s) => sum + calculatePay(s).net, 0);
    body.push(['', '', '', '', 'TOTAL', `N${total.toLocaleString('en-US', { minimumFractionDigits: 2 })}`]);

    autoTable(doc, {
      startY: 77,
      head: [['S/N', 'Staff Name', 'Dept', 'Bank', 'Acc Number', 'Net Amount']],
      body,
      theme: 'grid',
      styles: { fontSize: 9, cellPadding: 2, lineColor: [200, 200, 200], lineWidth: 0.1, textColor: 0 },
      headStyles: { fillColor: [41, 128, 185], textColor: 255, fontStyle: 'bold', fontSize: 10 },
      columnStyles: {
        0: { cellWidth: 15, halign: 'center' },
        1: { cellWidth: 55 },
        2: { cellWidth: 30 },
        3: { cellWidth: 35 },
        4: { cellWidth: 35 },
        5: { cellWidth: 35, halign: 'right' }
      },
      didParseCell: function(data) {
        if (data.row.index === body.length - 1) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = [241, 241, 241];
        }
      },
      margin: { left: 2, right: 5 }
    });

    doc.setFontSize(10);
    doc.text('Authorized Signature: _________________________', 15, doc.lastAutoTable.finalY + 20);
    doc.text(`Total Company Pay: N${total.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, 15, doc.lastAutoTable.finalY + 30);
    doc.text(`Generated: ${generatedDate}`, 105, 285, { align: 'center' });

    doc.save(`BankSummary_All_Departments_${monthName}_${selectedYear}.pdf`);
  };

  const generateBreakdownAll = () => {
    const doc = new jsPDF('landscape');
    addHeader(doc);

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(41, 128, 185);
    doc.text(`DETAILED SALARY BREAKDOWN - ${monthName.toUpperCase()} ${selectedYear}`, 148, 55, { align: 'center' });
    
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    doc.text(`All Departments`, 15, 60);

    const sortedStaff = [...data.staff].sort((a, b) => {
      const deptA = a.department || "Unassigned";
      const deptB = b.department || "Unassigned";
      if (deptA !== deptB) return deptA.localeCompare(deptB);
      return (a.firstName + " " + a.lastName).localeCompare(b.firstName + " " + b.lastName);
    });

    const allItems = new Set();
    sortedStaff.forEach(s => {
      const { items } = calculatePay(s);
      Object.keys(items).forEach(k => {
        if (k.toUpperCase() !== 'BASIC') allItems.add(k.toUpperCase());
      });
    });

    const earningsHeaders = ['BASIC'];
    Array.from(allItems).forEach(item => {
      if (sortedStaff.some(s => (calculatePay(s).items[item] || 0) > 0)) {
        earningsHeaders.push(item);
      }
    });

    const deductionsHeaders = Array.from(allItems)
      .filter(item => sortedStaff.some(s => (calculatePay(s).items[item] || 0) < 0))
      .sort();

    const headers = [
      'S/N', 'Employee Name', 'Department',
      ...earningsHeaders,
      'GROSS PAY',
      ...deductionsHeaders,
      'TOTAL DEDUCTIONS', 'NET PAY'
    ];

    const body = sortedStaff.map((s, i) => {
      const { gross, deductions, net, items, basic } = calculatePay(s);
      const row = [
        i + 1,
        `${s.firstName.toUpperCase()} ${s.lastName.toUpperCase()}`,
        s.department || 'N/A'
      ];

      earningsHeaders.forEach(header => {
        const amount = header === 'BASIC' ? basic : (items[header] || 0);
        row.push(`N${amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}`);
      });

      row.push(`N${gross.toLocaleString('en-US', { minimumFractionDigits: 2 })}`);

      deductionsHeaders.forEach(header => {
        const amount = items[header] || 0;
        row.push(`N${(amount < 0 ? -amount : 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`);
      });

      row.push(
        `N${deductions.toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
        `N${net.toLocaleString('en-US', { minimumFractionDigits: 2 })}`
      );

      return row;
    });

    // Total row
    const totalRow = ['', 'TOTAL', ''];
    earningsHeaders.forEach(header => {
      const sum = header === 'BASIC'
        ? sortedStaff.reduce((acc, s) => acc + (calculatePay(s).basic || 0), 0)
        : sortedStaff.reduce((acc, s) => acc + (calculatePay(s).items[header] || 0), 0);
      totalRow.push(`N${sum.toLocaleString('en-US', { minimumFractionDigits: 2 })}`);
    });

    const grossTotal = sortedStaff.reduce((a, s) => a + calculatePay(s).gross, 0);
    totalRow.push(`N${grossTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}`);

    deductionsHeaders.forEach(header => {
      const sum = sortedStaff.reduce((acc, s) => {
        const amt = calculatePay(s).items[header] || 0;
        return acc + (amt < 0 ? -amt : 0);
      }, 0);
      totalRow.push(`N${sum.toLocaleString('en-US', { minimumFractionDigits: 2 })}`);
    });

    const deductionsTotal = sortedStaff.reduce((a, s) => a + calculatePay(s).deductions, 0);
    const netTotal = sortedStaff.reduce((a, s) => a + calculatePay(s).net, 0);
    totalRow.push(
      `N${deductionsTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
      `N${netTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}`
    );

    body.push(totalRow);

    const earningsCount = earningsHeaders.length;
    const deductionsCount = deductionsHeaders.length;

    const columnStyles = {
      0: { cellWidth: 10, halign: 'center' },
      1: { cellWidth: 50 },
      2: { cellWidth: 35 },
      ...Object.fromEntries(Array.from({ length: earningsCount }, (_, i) => [3 + i, { halign: 'right', fillColor: [220, 237, 200] }])),
      [3 + earningsCount]: { halign: 'right', fontStyle: 'bold' },
      ...Object.fromEntries(Array.from({ length: deductionsCount }, (_, i) => [4 + earningsCount + i, { halign: 'right', fillColor: [245, 220, 220] }])),
      ...Object.fromEntries(Array.from({ length: 2 }, (_, i) => [4 + earningsCount + deductionsCount + i, { halign: 'right', fontStyle: 'bold' }]))
    };

    autoTable(doc, {
      startY: 65,
      head: [headers],
      body,
      theme: 'grid',
      styles: { fontSize: 7, cellPadding: 2, lineColor: [200, 200, 200], lineWidth: 0.1, textColor: 0 },
      headStyles: { fillColor: [41, 128, 185], textColor: 255, fontStyle: 'bold', fontSize: 8 },
      columnStyles,
      margin: { left: 5, right: 5 },
      didParseCell: function(data) {
        if (data.row.index === body.length - 1) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = [241, 241, 241];
        }
        const grossIdx = 3 + earningsCount;
        const dedTotIdx = 4 + earningsCount + deductionsCount;
        const netIdx = 5 + earningsCount + deductionsCount;
        if ([grossIdx, dedTotIdx, netIdx].includes(data.column.index)) {
          data.cell.styles.fontStyle = 'bold';
        }
      }
    });

    doc.setFontSize(9);
    doc.text('Authorized Signature: _________________________', 15, doc.lastAutoTable.finalY + 20);
    doc.text(`Generated: ${generatedDate}`, 148, 200, { align: 'center' });

    doc.save(`Breakdown_All_Departments_${monthName}_${selectedYear}.pdf`);
  };

  // ====================== RENDER ======================

  if (!userData || !['Admin', 'Supreme'].includes(userData.status)) {
    return <p className="male-female-stat">Access denied.</p>;
  }

  const totalNetPay = data.staff.reduce((sum, s) => sum + calculatePay(s).net, 0);

  return (
    <>
      {loading && <Loader text="Loading Salary Summary" />}

      <div className="top-elements row align-center space-between">
        <Link to="/home" className="back-link"><i className="fa-solid fa-arrow-left size3"></i></Link>
        <h2 className="manage-page-heading margin-top-cashflow1 caps size4">SALARY SUMMARY</h2>
        <Link to="#" className="home-link" onClick={fetchPayrollData}>
          <i className="fa-solid fa-rotate-right size4"></i>
        </Link>        
      </div>

      <div className="login-page-items login-page-items-cashflow column align-center scroll-fixed scroll-fixed6">
        <div className="row gap-15 align-center justify-center" style={{ marginTop: '10px', marginBottom: '-20px', maxWidth: '600px'}}>
          <select className="input-field" value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)}>
            {Array.from({ length: 12 }, (_, i) => (
              <option key={i+1} value={String(i+1).padStart(2, '0')}>
                {new Date(0, i).toLocaleString('default', { month: 'short' })}
              </option>
            ))}
          </select>
          <input type="number" className="input-field" value={selectedYear} onChange={e => setSelectedYear(e.target.value)} placeholder="Year" />
        </div>

        {data.staff.length > 0 && (
          <div className="">
            <p className="caps bold size5 text-center">Summary for {monthName} {selectedYear}</p>
            <div className="row gap-1 justify-center">
              <div className="stat-content stat-content-total-pay">                
                <p className="stat-value"><span className="italics size5">Total Pay</span><br /><span className="bold">₦{totalNetPay.toLocaleString()}</span></p>
              </div>
              <div className="stat-content">                
                <p className="stat-value"><span className="italics size5">Depts</span><br /><span className="bold">{data.departments.length}</span></p>
              </div>
              <div className="stat-content">                
                <p className="stat-value"><span className="italics size5">Staff</span><br /><span className="bold">{data.staff.length}</span></p>
              </div>              
            </div>
          </div>
        )}

        <div className="scroll-fixed scroll-fixed1">
          {/* ALL DEPARTMENTS ACCORDION - FIRST */}
          <div>
            <p className="input-field1 departments" onClick={() => setOpenDept(openDept === 'all' ? null : 'all')}>
              <span className="caps size5">ALL DEPARTMENTS ({data.staff.length} staff)</span><br />
              <span className="row align-center space-between gap-1">
                <strong>₦{totalNetPay.toLocaleString()}</strong>
                <i className={`fas fa-chevron-${openDept === 'all' ? 'up' : 'down'}`}></i>
              </span>
            </p>

            <div className={`accordion-content ${openDept === 'all' ? 'open' : ''}`} style={{ willChange: 'max-height, opacity' }}>
              <div className="row gap-15 flex-wrap summary-buttons">
                <button className="login-button caps" onClick={generatePDFPaySlipsAll}>
                  Pay Slips
                </button>
                <button className="login-button caps" onClick={generateBankSummaryAll}>
                  Bank Summary
                </button>
                <button className="login-button caps" onClick={generateBreakdownAll}>
                  Full Breakdown
                </button>
              </div>
            </div>
          </div>

          {/* INDIVIDUAL DEPARTMENTS */}
          {data.departments.map(dept => {
            const staffList = staffInDept(dept);
            const deptTotal = staffList.reduce((sum, s) => sum + calculatePay(s).net, 0);

            return (
              <div key={dept}>
                <p className="input-field1 departments" onClick={() => setOpenDept(openDept === dept ? null : dept)}>
                  <span className="caps size5">{dept} ({staffList.length} staff)</span><br />
                  <span className="row align-center space-between gap-1">
                    <strong>₦{deptTotal.toLocaleString()}</strong>
                    <i className={`fas fa-chevron-${openDept === dept ? 'up' : 'down'}`}></i>
                  </span>
                </p>

                <div 
                  className={`accordion-content ${openDept === dept ? 'open' : ''}`}
                  style={{ willChange: 'max-height, opacity' }}
                >
                  <div className="row gap-15 flex-wrap summary-buttons">
                    <button className="login-button caps" onClick={() => generatePDFPaySlips(dept)}>
                      Pay Slips
                    </button>
                    <button className="login-button caps" onClick={() => generateBankSummary(dept)}>
                      Bank Summary
                    </button>
                    <button className="login-button caps" onClick={() => generateBreakdown(dept)}>
                      Full Breakdown
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default Salary;